/**
 * AdminBooksMedia - Admin dashboard for Books & Media category
 * Includes moderation queue, pricing configuration, country settings
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  BookOpen, Newspaper, GraduationCap, BookImage, Disc, 
  Download, Gem, Pencil, Settings, Shield, DollarSign,
  AlertTriangle, CheckCircle, XCircle, Eye, Flag,
  TrendingUp, Users, Package, Clock, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  'books': BookOpen,
  'magazines': Newspaper,
  'educational-media': GraduationCap,
  'comics-graphic-novels': BookImage,
  'cds-dvds': Disc,
  'digital-media': Download,
  'collectibles': Gem,
  'stationery': Pencil
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {trend && (
            <p className={`text-sm mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}% from last week
            </p>
          )}
        </div>
        <div className={`p-4 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Moderation Item Component
const ModerationItem = ({ listing, onModerate }) => {
  const Icon = CATEGORY_ICONS[listing.category] || BookOpen;
  
  const getRiskBadge = (level) => {
    const styles = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700'
    };
    return styles[level] || styles.low;
  };
  
  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <Icon className="w-6 h-6 text-gray-600" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold">{listing.title}</h4>
                <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                  {listing.description}
                </p>
              </div>
              <Badge className={getRiskBadge(listing.risk_level)}>
                {listing.risk_level?.toUpperCase()} Risk
              </Badge>
            </div>
            
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span>Category: {listing.category}</span>
              <span>Price: {listing.country === 'UK' ? '£' : 'Rs.'}{listing.price}</span>
              <span>Location: {listing.location}</span>
            </div>
            
            {listing.risk_factors && listing.risk_factors.length > 0 && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-1">Risk Factors:</p>
                <ul className="text-sm text-amber-700 list-disc list-inside">
                  {listing.risk_factors.map((factor, i) => (
                    <li key={i}>{factor}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex gap-2 mt-4">
              <Button 
                size="sm" 
                onClick={() => onModerate(listing.id, 'approve')}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => onModerate(listing.id, 'reject')}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onModerate(listing.id, 'flag')}
              >
                <Flag className="w-4 h-4 mr-1" />
                Flag for Review
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Subcategory Config Row
const SubcategoryConfigRow = ({ slug, config, currencySymbol, onUpdate }) => {
  const Icon = CATEGORY_ICONS[slug] || BookOpen;
  const [localConfig, setLocalConfig] = useState(config);
  const [hasChanges, setHasChanges] = useState(false);
  
  const handleChange = (field, value) => {
    setLocalConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleSave = () => {
    onUpdate(slug, localConfig);
    setHasChanges(false);
  };
  
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-gray-100 rounded">
            <Icon className="w-4 h-4 text-gray-600" />
          </div>
          <span className="font-medium capitalize">{slug.replace(/-/g, ' ')}</span>
        </div>
      </TableCell>
      <TableCell>
        <Switch
          checked={localConfig.enabled}
          onCheckedChange={(v) => handleChange('enabled', v)}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={localConfig.commission_pct}
          onChange={(e) => handleChange('commission_pct', parseFloat(e.target.value))}
          className="w-20"
          min="0"
          max="100"
          step="0.5"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={localConfig.buyer_fee}
          onChange={(e) => handleChange('buyer_fee', parseFloat(e.target.value))}
          className="w-24"
          min="0"
          step="0.1"
        />
      </TableCell>
      <TableCell>
        <Select 
          value={localConfig.moderation_level || 'standard'}
          onValueChange={(v) => handleChange('moderation_level', v)}
        >
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">Light</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="strict">Strict</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Switch
          checked={localConfig.require_manual_review}
          onCheckedChange={(v) => handleChange('require_manual_review', v)}
        />
      </TableCell>
      <TableCell>
        {hasChanges && (
          <Button size="sm" onClick={handleSave}>Save</Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default function AdminBooksMedia() {
  const [country, setCountry] = useState('LK');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [config, setConfig] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [moderationStats, setModerationStats] = useState({});
  const [pricingConfig, setPricingConfig] = useState({});
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [offenders, setOffenders] = useState([]);
  const [digitalApprovals, setDigitalApprovals] = useState([]);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Fetch data
  useEffect(() => {
    fetchData();
  }, [country]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch config
      const configRes = await fetch(`${API_URL}/api/books-media/config/${country}`);
      const configData = await configRes.json();
      setConfig(configData.config);
      setPricingConfig(configData.config?.subcategories || {});
      
      // Fetch pending listings
      const pendingRes = await fetch(`${API_URL}/api/books-media/admin/pending?country=${country}`);
      const pendingData = await pendingRes.json();
      setPendingListings(pendingData.listings || []);
      
      // Calculate stats from data
      setStats({
        totalPending: pendingData.total || 0,
        highRisk: pendingData.listings?.filter(l => l.risk_level === 'high').length || 0,
        digitalMedia: pendingData.listings?.filter(l => l.category === 'digital-media').length || 0
      });
      
      // Get moderation stats
      setModerationStats({
        pending: pendingData.total || 0,
        high_risk: pendingData.listings?.filter(l => l.risk_level === 'high').length || 0,
        medium_risk: pendingData.listings?.filter(l => l.risk_level === 'medium').length || 0,
        low_risk: pendingData.listings?.filter(l => l.risk_level === 'low').length || 0
      });
      
      // Fetch offenders
      const offendersRes = await fetch(`${API_URL}/api/books-media/admin/offenders?country=${country}`);
      const offendersData = await offendersRes.json();
      setOffenders(offendersData.offenders || []);
      
      // Fetch digital media approvals
      const approvalsRes = await fetch(`${API_URL}/api/books-media/digital-media/pending-approvals?country=${country}`);
      const approvalsData = await approvalsRes.json();
      setDigitalApprovals(approvalsData.approvals || []);
      
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle moderation action
  const handleModerate = async (listingId, action, reason = '') => {
    try {
      const response = await fetch(`${API_URL}/api/books-media/admin/moderate/${listingId}?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason })
      });
      
      if (!response.ok) throw new Error('Moderation failed');
      
      toast.success(`Listing ${action}d successfully`);
      
      // Remove from pending list
      setPendingListings(prev => prev.filter(l => l.id !== listingId));
      setStats(prev => ({ ...prev, totalPending: prev.totalPending - 1 }));
      
    } catch (err) {
      toast.error('Failed to moderate listing');
    }
  };
  
  // Handle config update
  const handleConfigUpdate = async (slug, newConfig) => {
    try {
      // In a real implementation, this would call a backend API
      setPricingConfig(prev => ({ ...prev, [slug]: newConfig }));
      toast.success(`Configuration updated for ${slug}`);
    } catch (err) {
      toast.error('Failed to update configuration');
    }
  };
  
  // Toggle digital media
  const toggleDigitalMedia = async (enabled) => {
    try {
      // In a real implementation, this would call a backend API
      setConfig(prev => ({ ...prev, digital_media_enabled: enabled }));
      toast.success(`Digital Media ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update Digital Media setting');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Books & Media Admin</h1>
              <p className="text-gray-500">Manage listings, pricing, and moderation</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LK">Sri Lanka</SelectItem>
                <SelectItem value="UK">United Kingdom</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Pending Review"
            value={stats.totalPending || 0}
            icon={Clock}
            color="bg-amber-500"
          />
          <StatCard
            title="High Risk"
            value={stats.highRisk || 0}
            icon={AlertTriangle}
            color="bg-red-500"
          />
          <StatCard
            title="Digital Media"
            value={stats.digitalMedia || 0}
            icon={Download}
            color="bg-purple-500"
          />
          <StatCard
            title="Digital Enabled"
            value={config?.digital_media_enabled ? 'Yes' : 'No'}
            icon={Shield}
            color={config?.digital_media_enabled ? 'bg-green-500' : 'bg-gray-500'}
          />
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="moderation">
              Moderation Queue
              {stats.totalPending > 0 && (
                <Badge variant="destructive" className="ml-2">{stats.totalPending}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pricing">Pricing & Fees</TabsTrigger>
            <TabsTrigger value="offenders">
              Offenders
              {offenders.length > 0 && (
                <Badge variant="destructive" className="ml-2">{offenders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="digital">
              Digital Approvals
              {digitalApprovals.length > 0 && (
                <Badge className="ml-2 bg-purple-500">{digitalApprovals.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Risk Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Risk Distribution</CardTitle>
                  <CardDescription>Pending listings by risk level</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span>Low Risk</span>
                      </div>
                      <span className="font-semibold">{moderationStats.low_risk || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span>Medium Risk</span>
                      </div>
                      <span className="font-semibold">{moderationStats.medium_risk || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>High Risk</span>
                      </div>
                      <span className="font-semibold">{moderationStats.high_risk || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Common admin tasks</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('moderation')}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                    Review High-Risk Listings ({stats.highRisk || 0})
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('pricing')}
                  >
                    <DollarSign className="w-4 h-4 mr-2 text-green-500" />
                    Configure Pricing
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('settings')}
                  >
                    <Settings className="w-4 h-4 mr-2 text-blue-500" />
                    Country Settings
                  </Button>
                </CardContent>
              </Card>
              
              {/* Digital Media Control */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-red-500" />
                    Digital Media Control
                  </CardTitle>
                  <CardDescription>
                    Digital Media is a high-risk category requiring strict controls
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Alert className={config?.digital_media_enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                    <AlertDescription className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          Digital Media is currently {config?.digital_media_enabled ? 'ENABLED' : 'DISABLED'} for {country}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          When enabled, all digital media listings require manual review before going live.
                        </p>
                      </div>
                      <Switch
                        checked={config?.digital_media_enabled || false}
                        onCheckedChange={toggleDigitalMedia}
                      />
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <Card>
              <CardHeader>
                <CardTitle>Moderation Queue</CardTitle>
                <CardDescription>
                  {pendingListings.length} listings pending review
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : pendingListings.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold">All caught up!</h3>
                    <p className="text-gray-500">No listings pending review</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingListings.map(listing => (
                      <ModerationItem
                        key={listing.id}
                        listing={listing}
                        onModerate={handleModerate}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle>Pricing & Commission Configuration</CardTitle>
                <CardDescription>
                  Configure fees per subcategory. Changes apply to new listings only.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Subcategory</TableHead>
                        <TableHead>Enabled</TableHead>
                        <TableHead>Commission %</TableHead>
                        <TableHead>Buyer Fee ({currencySymbol})</TableHead>
                        <TableHead>Moderation</TableHead>
                        <TableHead>Manual Review</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(pricingConfig).map(([slug, cfg]) => (
                        <SubcategoryConfigRow
                          key={slug}
                          slug={slug}
                          config={cfg}
                          currencySymbol={currencySymbol}
                          onUpdate={handleConfigUpdate}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <Alert className="mt-6">
                  <AlertDescription>
                    <strong>Commission Model:</strong> Seller commission is charged when item is sold. 
                    Buyer fee is a fixed amount added at checkout. 
                    Default: 8-12% seller commission depending on subcategory.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* General Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>General Settings ({country})</CardTitle>
                  <CardDescription>Country-specific configuration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Currency</Label>
                      <p className="text-sm text-gray-500">{currencySymbol}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Featured Listing Price</Label>
                      <p className="text-sm text-gray-500">Cost to feature a listing for 7 days</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold">
                        {currencySymbol}{config?.featured_price || 500}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Escrow Available</Label>
                      <p className="text-sm text-gray-500">For high-value collectibles</p>
                    </div>
                    <Badge variant={config?.escrow_available ? 'default' : 'secondary'}>
                      {config?.escrow_available ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
              
              {/* Piracy Detection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-500" />
                    Piracy Detection
                  </CardTitle>
                  <CardDescription>Keywords that trigger automatic actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-red-600">Hard Block Keywords (Auto-Reject)</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        pdf download, free pdf, torrent, mega.nz, cracked, pirated, bootleg
                      </p>
                    </div>
                    <div>
                      <Label className="text-amber-600">Soft Flag Keywords (Manual Review)</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        photocopy, xerox, scan, shared account, cheap ebook
                      </p>
                    </div>
                    <div>
                      <Label className="text-purple-600">Counterfeit Indicators</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        replica, reprint as original, like original, copy of signed
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Risk Score Thresholds */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Risk Score Thresholds</CardTitle>
                  <CardDescription>How listings are routed based on risk</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-700">Low Risk (0-24)</h4>
                      <p className="text-sm text-green-600 mt-1">
                        Auto-approved immediately
                      </p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <h4 className="font-semibold text-yellow-700">Medium Risk (25-59)</h4>
                      <p className="text-sm text-yellow-600 mt-1">
                        Flagged for manual review
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="font-semibold text-red-700">High Risk (60+)</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Auto-rejected if blocked keywords found, otherwise flagged
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Offenders Tab */}
          <TabsContent value="offenders">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Repeat Offenders
                </CardTitle>
                <CardDescription>Users with policy violations</CardDescription>
              </CardHeader>
              <CardContent>
                {offenders.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <p className="text-gray-500">No offenders recorded</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Strikes</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Violation</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {offenders.map(offender => (
                        <TableRow key={offender.id}>
                          <TableCell>{offender.email}</TableCell>
                          <TableCell>
                            <Badge variant={offender.strikes >= 3 ? 'destructive' : 'secondary'}>
                              {offender.strikes} strikes
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              offender.status === 'permanently_banned' ? 'bg-red-500' :
                              offender.status === 'temp_banned' ? 'bg-orange-500' :
                              offender.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                            }>
                              {offender.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {offender.last_violation ? new Date(offender.last_violation).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {offender.status !== 'good_standing' && (
                              <Button size="sm" variant="outline">Lift Ban</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                
                <Alert className="mt-6">
                  <AlertDescription>
                    <strong>Strike Policy:</strong> 1st = Warning, 2nd = 7-day ban, 3rd = Permanent ban
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Digital Media Approvals Tab */}
          <TabsContent value="digital">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-purple-500" />
                  Digital Media Approval Queue
                </CardTitle>
                <CardDescription>Review digital media listings for compliance</CardDescription>
              </CardHeader>
              <CardContent>
                {!config?.digital_media_enabled ? (
                  <Alert className="mb-4 bg-amber-50 border-amber-200">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <AlertDescription className="text-amber-700">
                      Digital Media is currently <strong>disabled</strong> for {country}. Enable it in Settings to accept submissions.
                    </AlertDescription>
                  </Alert>
                ) : null}
                
                {digitalApprovals.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <p className="text-gray-500">No pending digital media approvals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {digitalApprovals.map(approval => (
                      <Card key={approval.id} className="border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium">Listing #{approval.listing_id?.slice(-8)}</h4>
                              <p className="text-sm text-gray-500 mt-1">
                                License: {approval.license_type} | Source: {approval.content_source}
                              </p>
                            </div>
                            <Badge className="bg-purple-500">Pending Review</Badge>
                          </div>
                          
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium mb-2">Compliance Checklist:</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="rounded" />
                                No pirated content
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="rounded" />
                                Valid license
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="rounded" />
                                No malware
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="rounded" />
                                Age appropriate
                              </label>
                              <label className="flex items-center gap-2">
                                <input type="checkbox" className="rounded" />
                                Accurate description
                              </label>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-4">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button size="sm" variant="destructive">
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
